import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config/api';

export default function Leaderboard({ onNavigate }) {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    loadLeaderboard();
    // Refresh every 5 minutes
    const interval = setInterval(loadLeaderboard, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leaderboard`);
      const data = await response.json();
      setLeaderboard(data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch(rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
      Loading leaderboard...
    </div>;
  }

  if (!leaderboard || !leaderboard.leaderboard.length) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
      No tokens registered yet
    </div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ color: '#A78BFA', marginBottom: '8px' }}>Token Leaderboard</h2>
      <p style={{ color: '#9ca3af', marginBottom: '24px', lineHeight: '1.6' }}>
        All registered XRPL tokens ranked by tokens locked. Tokens marked as "Pending" are awaiting admin approval.
      </p>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '8px' }}>
            Total TVL
          </div>
          <div style={{ color: '#4ade80', fontSize: '2rem', fontWeight: '700' }}>
            ${leaderboard.leaderboard.reduce((sum, token) => sum + (token.tvl_usd || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '8px' }}>
            Total Tokens
          </div>
          <div style={{ color: '#A78BFA', fontSize: '2rem', fontWeight: '700' }}>
            {leaderboard.total_tokens}
          </div>
        </div>

        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '8px' }}>
            Top Token
          </div>
          <div style={{ color: '#FFB84D', fontSize: '1.5rem', fontWeight: '700' }}>
            {leaderboard.leaderboard[0]?.symbol ? `$${leaderboard.leaderboard[0].symbol}` : 'N/A'}
          </div>
        </div>

        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '8px' }}>
            Last Updated
          </div>
          <div style={{ color: '#fff', fontSize: '0.9rem' }}>
            {new Date(leaderboard.timestamp).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div style={{
        backgroundColor: '#2a2a2a',
        border: '1px solid #3a3a3a',
        borderRadius: '12px',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : 'auto' }}>
          <thead>
            <tr style={{ backgroundColor: '#1a1a1a' }}>
              <th style={headerStyle}>Rank</th>
              <th style={headerStyle}>Token</th>
              <th style={headerStyle}>Tokens Locked</th>
              <th style={headerStyle}>TVL (USD)</th>
              {!isMobile && <th style={headerStyle}>Contract</th>}
            </tr>
          </thead>
          <tbody>
            {leaderboard.leaderboard.map((token, i) => (
              <tr key={token.symbol} style={{
                borderTop: i > 0 ? '1px solid #3a3a3a' : 'none',
                backgroundColor: i < 3 ? 'rgba(167, 139, 250, 0.05)' : 'transparent'
              }}>
                <td style={{ ...cellStyle, textAlign: 'center', fontSize: '1.2rem' }}>
                  {getRankIcon(token.rank)}
                </td>
                <td style={cellStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {token.image_url ? (
                      <img
                        src={token.image_url}
                        alt={token.symbol}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #3a3a3a'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#3a3a3a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: '700',
                        color: '#A78BFA'
                      }}>
                        {token.symbol.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '700', color: '#A78BFA', fontSize: '1rem' }}>
                          ${token.symbol}
                        </span>
                        {token.status === 'pending' && (
                          <span style={{
                            fontSize: '0.65rem',
                            backgroundColor: '#fb923c',
                            color: '#1a1a1a',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            Pending
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {token.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={cellStyle}>
                  <span style={{ color: '#9ca3af', fontWeight: '600' }}>
                    {token.tvl.toLocaleString()}
                  </span>
                </td>
                <td style={cellStyle}>
                  <span style={{ color: '#4ade80', fontWeight: '700', fontSize: '1rem' }}>
                    {token.tvl > 0 && token.tvl_usd >= 0 ? (
                      token.tvl_usd < 0.01 && token.tvl_usd > 0
                        ? `$${token.tvl_usd.toFixed(6)}`
                        : `$${token.tvl_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    ) : '-'}
                  </span>
                </td>
                {!isMobile && (
                  <td style={cellStyle}>
                    {token.flare_contract ? (
                      <a
                        href={`https://flare-explorer.flare.network/address/${token.flare_contract}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#A78BFA',
                          textDecoration: 'none',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace'
                        }}
                      >
                        {token.flare_contract.substring(0, 6)}...{token.flare_contract.substring(38)}
                      </a>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                        Not deployed
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Info Section */}
      <div style={{ marginTop: '32px', padding: '20px', backgroundColor: '#1a1a1a', borderRadius: '12px' }}>
        <h3 style={{ color: '#A78BFA', marginBottom: '12px', fontSize: '1.1rem' }}>
          About the Leaderboard
        </h3>
        <p style={{ color: '#9ca3af', fontSize: '0.875rem', lineHeight: '1.6' }}>
          <strong style={{ color: '#fff' }}>Tokens Locked:</strong> The total amount of tokens locked in XRPL vaults.<br/>
          <strong style={{ color: '#fff' }}>TVL (Total Value Locked):</strong> The USD value of locked tokens based on current market prices.
          Higher TVL indicates more users trust and use the token on our platform.
        </p>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '48px',
        paddingTop: '24px',
        borderTop: '1px solid #3a3a3a',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.875rem'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <span
            onClick={() => onNavigate && onNavigate('terms')}
            style={{
              color: '#A78BFA',
              textDecoration: 'none',
              marginRight: '24px',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            Terms & Conditions
          </span>
          <span
            onClick={() => onNavigate && onNavigate('privacy')}
            style={{
              color: '#A78BFA',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            Privacy Policy
          </span>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontWeight: '600', color: '#fff' }}>Contact Us: </span>
          <a
            href="mailto:support@EmberX.xyz"
            style={{
              color: '#FFB84D',
              textDecoration: 'none',
              marginRight: '16px'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            ✉️ support@EmberX.xyz
          </a>
          <span style={{ marginRight: '8px' }}>|</span>
          <a
            href="https://x.com/EmberXFi"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#FFB84D',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '2px' }}>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            @EmberXFi
          </a>
        </div>
        <p style={{ margin: '0' }}>
          © 2025 EmberX. All rights reserved.
        </p>
      </div>
    </div>
  );
}

const headerStyle = {
  padding: '16px',
  textAlign: 'left',
  color: '#9ca3af',
  fontSize: '0.875rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const cellStyle = {
  padding: '16px',
  color: '#fff',
  fontSize: '0.9rem'
};
